
import { BaseRepository } from '@/shared/infrastructure/BaseRepository';

interface ReferralData {
  id: string;
  referrerId: string;
  referredName: string;
  whatsappNumber: string;
  status: string;
  convertedAt?: Date;
  convertedSessionsCount: number;
  createdAt: Date;
  updatedAt: Date;
}

interface CreateReferralData {
  referrerId: string;
  referredName: string;
  whatsappNumber: string;
  status?: string;
}

export class ReferralRepository extends BaseRepository {

  async findById(id: string): Promise<ReferralData | null> {
    const { data, error } = await this.getClient()
      .from('referrals')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error(`Erro ao buscar indicação por ID: ${error.message}`);
      return null;
    }

    return data ? this.mapToReferralData(data) : null;
  }

  async findByUserId(userId: string): Promise<ReferralData[]> {
    const { data, error } = await this.getClient()
      .from('referrals')
      .select('*')
      .eq('referrer_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar indicações por usuário: ${error.message}`);
    }

    return (data || []).map(item => this.mapToReferralData(item));
  }

  async findByReferrerId(referrerId: string): Promise<ReferralData[]> {
    return this.findByUserId(referrerId);
  }

  async findByWhatsappNumber(whatsappNumber: string): Promise<ReferralData | null> {
    const { data, error } = await this.getClient()
      .from('referrals')
      .select('*')
      .eq('whatsapp_number', whatsappNumber)
      .maybeSingle();

    if (error) {
      console.error(`Erro ao buscar indicação por WhatsApp: ${error.message}`);
      return null;
    }

    return data ? this.mapToReferralData(data) : null;
  }

  async create(data: CreateReferralData): Promise<ReferralData> {
    const { data: createdData, error } = await this.getClient()
      .from('referrals')
      .insert({
        referrer_id: data.referrerId,
        referred_name: data.referredName,
        whatsapp_number: data.whatsappNumber,
        status: data.status || 'pending'
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar indicação: ${error.message}`);
    }

    return this.mapToReferralData(createdData);
  }

  async createForUser(userId: string, data: CreateReferralData): Promise<ReferralData> {
    return this.create({ ...data, referrerId: userId });
  }

  private mapToReferralData(data: any): ReferralData {
    return {
      id: data.id,
      referrerId: data.referrer_id,
      referredName: data.referred_name,
      whatsappNumber: data.whatsapp_number,
      status: data.status,
      convertedAt: data.converted_at ? new Date(data.converted_at) : undefined,
      convertedSessionsCount: data.converted_sessions_count || 0,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }
}
